import type {
  ClassAttributes,
  HTMLAttributes,
  KeyboardEventHandler,
  MouseEventHandler,
  MouseEvent,
} from "react";
import { forwardRef, useRef } from "react";
import { useInView } from "react-intersection-observer";
import { composeRefs } from "@exonn/hooks";
import { Text } from "src/components/Text/Text.tsx";

import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { css } from "@emotion/react";
import { useTabsStore } from "src/components/Tabs/TabsProvider.tsx";
import type { Tab as TabModel } from "src/lib/tabs-ui/TabsStore.tsx";
import { observer } from "mobx-react-lite";

export type TabProps = {
  tab: TabModel;
  showPlusButton: boolean;
} & HTMLAttributes<HTMLButtonElement> &
  ClassAttributes<HTMLButtonElement>;

export const Tab = observer(
  forwardRef<HTMLButtonElement, TabProps>((props, ref) => {
    const { tab, showPlusButton, ...rest } = props;
    const localRef = useRef<HTMLButtonElement | null>(null);
    const store = useTabsStore();

    const activeTab = store.activeTab;

    const { ref: connectIntersectionObserver } = useInView({
      root: store.containerEl,
      threshold: 0.99,
      onChange: (inView, entry) => {
        if (!store.containerEl?.contains(localRef.current)) {
          return;
        }
        tab.setIsOutOfView(!inView);
      },
    });

    const handleKeyDown: KeyboardEventHandler<HTMLButtonElement> = (e) => {
      if (e.keyCode === 8) {
        tab.close();
      }
    };

    const handleCloseButtonClick: MouseEventHandler<HTMLButtonElement> = (
      e,
    ) => {
      e.stopPropagation();
      e.preventDefault();
      tab.close();
    };

    const handleTriggerClick = (e: MouseEvent) => {
      tab.activate({
        emitEvents: true,
        updateState: !store.hasControlledTabId,
      });
    };

    const isClosable = !tab.isPinned && tab.isClosable;

    const handleCloseButtonMouseDown = (e: MouseEvent<HTMLSpanElement>) => {
      // prevents the tab from being activated when the close button is clicked
      e.preventDefault();
    };

    const handleAuxClick = (e: MouseEvent<HTMLButtonElement>) => {
      // middle click (wheel click)
      if (e.button !== 1) {
        return;
      }
      if (!tab.isClosable) {
        return;
      }
      tab.close();
    };

    return (
      <div>
        <button
          css={triggerStyles({
            activeTabOffset: (activeTab?.index || Infinity) - tab.index,
            isClosable: isClosable,
            maxWidth: tab.maxWidth,
            minWidth: tab.minWidth,
          })}
          data-state={tab.isActive ? "active" : "inactive"}
          {...rest}
          onAuxClick={handleAuxClick}
          onClick={handleTriggerClick}
          ref={composeRefs(
            ref,
            (node) => (localRef.current = node),
            (node) => connectIntersectionObserver(node),
          )}
          onKeyDown={handleKeyDown}
          value={tab.id}
        >
          <Separator tab={tab} showPlusButton={showPlusButton} />
          {tab.favicon}
          <Text css={titleStyles} variant="body2">
            <span className={"tab-title"}>{tab.title}</span>
          </Text>
          <span
            css={unsavedChangesIndicatorStyles({
              visible: tab.hasUnsavedChanges,
            })}
          />

          {isClosable && (
            <span
              onMouseDown={handleCloseButtonMouseDown}
              css={closeButtonStyles}
              className="tab-close-icon"
              onClick={handleCloseButtonClick}
            >
              <i className={"icon-fl-15-close"} />
            </span>
          )}
        </button>
      </div>
    );
  }),
);

const Separator = observer(function SeparatorObserved({
  tab,
  showPlusButton,
}: {
  tab: TabModel;
  showPlusButton: boolean;
}) {
  const store = useTabsStore();
  const activeTab = store.activeTab;

  const hideSeparator =
    tab.id === activeTab?.id ||
    tab.id === store.hoveredTab?.id ||
    (tab.next && tab.next.id === activeTab?.id) ||
    (tab.next && tab.next.id === store.hoveredTab?.id) ||
    (tab.isLast && !showPlusButton);

  if (hideSeparator) {
    return null;
  }
  return <div css={separatorStyles} />;
});

const separatorStyles = (theme: ExactTheme) => css`
  position: absolute;
  right: -1px;
  height: 16px;
  top: 50%;
  transform-origin: center;
  transform: translateY(-50%);
  width: 1px;
  background: ${theme.colors.border.muted2};
`;

const closeButtonStyles = (theme: ExactTheme) => css`
  display: none;
  position: absolute;
  padding: 0;
  border: none;
  background: transparent;
  top: 50%;
  transform-origin: center;
  transform: translateY(-50%);
  right: 8px;
  width: 16px;
  height: 16px;
  color: ${theme.colors.gray[300]};

  :hover {
    color: ${theme.colors.danger.fg};
  }
`;

const triggerStyles =
  ({
    activeTabOffset,
    isClosable,
    maxWidth,
    minWidth,
  }: {
    activeTabOffset: number;
    isClosable: boolean;
    maxWidth?: number;
    minWidth?: number;
  }) =>
  (theme: ExactTheme) => [
    css`
      position: relative;
      padding: 0 14px;
      outline: none;
      display: flex;
      align-items: center;
      height: 38px;
      margin: 0;
      cursor: pointer;
      border: 1px solid transparent;
      background-color: transparent;
      border-bottom: none;
      color: ${theme.colors.fg.muted};
      white-space: nowrap;
      max-width: ${maxWidth ? `${maxWidth}px` : "unset"};
      min-width: ${minWidth ? `${minWidth}px` : 0};

      &[data-state="active"] {
        background-color: ${theme.colors.canvas.subtle};
        color: ${theme.colors.fg.default};
        border: 1px solid ${theme.colorsV2.grey[100]};
        border-bottom: none;

        :after {
          content: "";
          position: absolute;
          top: -1px;
          height: 2px;
          left: -1px;
          right: -1px;
          background-color: ${theme.colors.accent.fg};
        }
      }

      &[data-state="inactive"] {
        &:hover {
          border: 1px solid ${theme.colors.border.default};
          border-bottom: none;
          background-color: ${theme.colors.gray[25]};
          color: ${theme.colors.fg.default};

          border-right: 1px solid
            ${activeTabOffset === 1 ? "white" : theme.colors.border.default};

          border-left: 1px solid
            ${activeTabOffset === -1 ? "white" : theme.colors.border.default};
        }
      }
    `,

    isClosable &&
      css`
        .tab-title {
          display: block;
          text-overflow: ellipsis;
          overflow: hidden;
        }

        &:hover {
          .tab-close-icon {
            display: flex;
          }

          .tab-title {
            width: calc(100% - 8px); // hack to truncate the tab title
          }
        }
      `,
  ];

const titleStyles = css`
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  overflow: hidden;
  flex-grow: 1;
`;

const unsavedChangesIndicatorStyles =
  ({ visible }: { visible: boolean }) =>
  (theme: ExactTheme) =>
    css({
      position: "absolute",
      width: "5px",
      height: "5px",
      top: "5px",
      right: "3px",
      backgroundColor: theme.colorsV2.grey[300],
      opacity: visible ? 1 : 0,
      borderRadius: "100%",
    });
